import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import {DocumentNode} from 'graphql';
import gql from 'graphql-tag';
import React, {useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {Owner} from 'auth/PermissionConstants';
import {SavingState} from 'constants/RegistryConstants';
import {EntitySummary} from 'constants/UserConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {DATA_TEST_ATTRS} from 'instruments/connection-schemas/ConnectionSchemaMetadataSection';
import {NameTemplatePart, NameTemplateType} from 'name-templates/NameTemplateConstants';
import ProcedureRunNameTemplateField from 'procedures/name_templates/ProcedureRunNameTemplateField';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {ClientSchemaField} from 'registry/field/FieldTable';
import FieldTable from 'registry/field/FieldTable';
import SchemaDescriptionInput from 'schemas/SchemaDescriptionInput';
import SchemaNameInput from 'schemas/SchemaNameInput';
import SchemaOwnerDisplay from 'schemas/SchemaOwnerDisplay';
import {getOwnerById} from 'schemas/SchemaUtil';
import SchemaWarehouseNameField from 'schemas/SchemaWarehouseNameField';
import {filterSchemasByRegistryOrganization} from 'schemas/util/FilterSchemasByOrganization';
import StudySchemaFieldTableRow from 'studies/study-schemas/StudySchemaFieldTableRow';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {getEnabledStudyTypes, isClinicalOrPreclinicalStudy} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {SILVER, GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {SettingsLabel} from 'ui-library/SettingsField';

import {getProcedureTypeForStudyTypeAllowNull} from '../TypedStudyUtils';
import {StudySchemaMetadataSettingsQuery} from './__generated/StudySchemaMetadataSettingsQuery';
import {StudySchemaQuery} from './__generated/StudySchemaQuery';
import {StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates as EntryTemplateIconOrInaccessibleInfo} from './__generated/StudySchemaQuery';
import SetEntryTemplatesOnStudySchemasModal from './SetEntryTemplatesOnStudySchemasModal';
import StudyNameTemplateField from './StudyNameTemplateField';
import {SETTINGS_GRID_COLUMN_GAP_PERCENT, SETTINGS_GRID_COLUMN_WIDTH_PERCENT} from './StudySchemaConstants';
import StudySchemaEntryTemplatesTable from './StudySchemaEntryTemplatesTable';
import StudySchemaGeneralSettingsEditor from './StudySchemaGeneralSettingsEditor';
import StudySchemaIdGenerationSettings from './StudySchemaIdGenerationSettings';
import StudySchemaTypeDropdown from './StudySchemaTypeDropdown';
type StudySchema = ExtractByTypename<StudySchemaQuery['studySchema'], 'StudySchemaNode'>;

// Exported for testing only
export function Row({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.schemaMetadataRow)}>{children}</div>;
}

function Col({children}: {children?: JSX.Element}): JSX.Element {
  return <div className={css(styles.schemaMetadataColumn)}>{children}</div>;
}

function FieldWrapper({children}: {children: JSX.Element}): JSX.Element {
  return <div className={css(styles.schemaMetadataField)}>{children}</div>;
}

export const STUDY_SCHEMA_METADATA_SETTINGS_QUERY: DocumentNode = gql`
  query StudySchemaMetadataSettingsQuery @skipAudit @engComponent(name: STUDIES) {
    boxSchemas {
      ...BoxSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    containerSchemas {
      ...ContainerSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    locationSchemas {
      ...LocationSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
    plateSchemas {
      ...PlateSchemaGraphQL
      registry {
        ...RegistryGraphQL
      }
    }
  }
  ${getFragmentWrapper('BoxSchema').fragment}
  ${getFragmentWrapper('ContainerSchema').fragment}
  ${getFragmentWrapper('LocationSchema').fragment}
  ${getFragmentWrapper('PlateSchema').fragment}
  ${getFragmentWrapper('Registry').fragment}
`;

export interface StudySchemaMetadataSettingsProps {
  ownerId: string;
  setOwnerId: (id: string) => void;
  name: string;
  setName: (id: string) => void;
  schemaDescription: string;
  setSchemaDescription: (schemaDescription: string) => void;
  studyNameTemplateParts: Array<NameTemplatePart>;
  setStudyNameTemplateParts: (parts: Array<NameTemplatePart>) => Promise<boolean>;
  procedureRunNameTemplateParts: Array<NameTemplatePart>;
  setProcedureRunNameTemplateParts: (parts: Array<NameTemplatePart>) => Promise<boolean>;
  prefix: string;
  fields: Array<ClientSchemaField>;
  studyType: StudyType;
  setStudyType: (studyType: StudyType) => void;
  setFields: (fields: Array<ClientSchemaField>) => void;
  isDesignApprovalRequired: boolean;
  setIsDesignApprovalRequired: (isDesignApprovalRequired: boolean) => void;
  handleNext?: () => void;
  ownerChoices: Array<Owner>;
  isBeingCreated: boolean;
  schemaId: string | null;
  schema?: StudySchema;
  entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>;
  setEntryTemplates: (entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>) => void;
  autoGenerateId: boolean;
  onIdGenerationChange: (autoGenerateId: boolean, prefix: string) => void | Promise<boolean>;
  setSqlIdentifier?: (sqlIdentifier: string) => void;
}

export const NAMING_TEMPLATE_TOOLTIP_MESSAGE =
  'You can automatically name studies based on a naming template once this schema has been created. If the name template is changed, it will not affect the names of existing studies.';

export const SET_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE = 'Set after creating study schema';
export const CUSTOMIZE_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE =
  'Customize after creating study schema';

export function getProcedureRunNameTemplateTooltipText(procedureType: ProcedureType | null): string {
  assert(procedureType != null, 'Procedure type is required to get procedure run name template tooltip text');
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  return `You can automatically name ${procedureString} runs based on a naming template once this schema has been created. If the name template is changed, it will not affect the names of existing ${procedureString} runs.`;
}

export function NameTemplatesHelpIcon({
  nameTemplateType,
  procedureType,
}: {
  nameTemplateType: NameTemplateType.PROCEDURE_RUN | NameTemplateType.STUDY;
  procedureType: ProcedureType | null;
}): JSX.Element {
  const text =
    nameTemplateType === NameTemplateType.STUDY
      ? NAMING_TEMPLATE_TOOLTIP_MESSAGE
      : getProcedureRunNameTemplateTooltipText(procedureType);
  return (
    <div>
      <Tooltip text={text}>
        <BenchlingIcon className={css(styles.infoIcon)} icon="more-info" />
      </Tooltip>
    </div>
  );
}
/**
 * Presentational component for configuring a StudySchema, including both system fields and schema fields.
 */
export default function StudySchemaMetadataSettings({
  ownerId,
  setOwnerId,
  name,
  setName,
  schemaDescription,
  setSchemaDescription,
  studyNameTemplateParts,
  setStudyNameTemplateParts,
  procedureRunNameTemplateParts,
  setProcedureRunNameTemplateParts,
  prefix,
  fields,
  setFields,
  isDesignApprovalRequired,
  setIsDesignApprovalRequired,
  studyType,
  setStudyType,
  handleNext,
  ownerChoices,
  isBeingCreated,
  schemaId,
  schema,
  entryTemplates,
  setEntryTemplates,
  autoGenerateId,
  onIdGenerationChange,
  setSqlIdentifier,
}: StudySchemaMetadataSettingsProps): JSX.Element {
  const handleOnAddClick: () => Promise<void> = async () => {
    await legacyRunModal(SetEntryTemplatesOnStudySchemasModal, {
      setEntryTemplates,
      selectedTemplates: entryTemplates,
    });
  };

  let error = null;
  if (!name) {
    error = 'Schema needs a name';
  } else if (!prefix && autoGenerateId) {
    error = 'Schema needs a prefix';
  } else if (fields?.some((f) => !f.name)) {
    error = 'Schema fields must all have names';
  }

  function filterDataToOrganization(
    queryResult: StudySchemaMetadataSettingsQuery,
    organizationId: string
  ): StudySchemaMetadataSettingsQuery {
    const data = {...queryResult};
    data.boxSchemas = filterSchemasByRegistryOrganization(queryResult.boxSchemas, organizationId);
    data.containerSchemas = filterSchemasByRegistryOrganization(queryResult.containerSchemas, organizationId);
    data.locationSchemas = filterSchemasByRegistryOrganization(queryResult.locationSchemas, organizationId);
    data.plateSchemas = filterSchemasByRegistryOrganization(queryResult.plateSchemas, organizationId);

    return data;
  }

  const queryResult = useBenchlingQuery<StudySchemaMetadataSettingsQuery>(
    STUDY_SCHEMA_METADATA_SETTINGS_QUERY
  );
  const linkableSchemas = useMemo(() => {
    if (queryResult.type === QueryResultType.SUCCESS) {
      const data = filterDataToOrganization(queryResult.data, ownerId);

      return {
        storableSchemas: [
          ...graphQLArrayToREST('ContainerSchema', data.containerSchemas),
          ...graphQLArrayToREST('BoxSchema', data.boxSchemas),
          ...graphQLArrayToREST('LocationSchema', data.locationSchemas),
          ...graphQLArrayToREST('PlateSchema', data.plateSchemas),
        ],
      };
    }
    return {
      storableSchemas: [],
    };
  }, [queryResult, ownerId]);

  // Used to determine the copy shown for the not-yet-creatable run name template field.
  const isClinicalOrPreclinical = isClinicalOrPreclinicalStudy(studyType);

  const procedureType = getProcedureTypeForStudyTypeAllowNull(studyType);
  // Used to determine if the current study schema type is allowed to have a run name template field. This excludes custom studies.
  const isProcedureBasedStudy = procedureType != null;
  // Conditionally render the run name template field based on the study type, schema creation state, and feature flag.
  const maybeRunNameTemplateField = isProcedureBasedStudy && (
    <Col key="ProcedureRunNameTemplateFieldColumn">
      {!isBeingCreated && schemaId != null ? (
        <FieldWrapper>
          <ProcedureRunNameTemplateField
            nameTemplateParts={procedureRunNameTemplateParts}
            schemaId={schemaId}
            studyType={studyType}
            isWritable={true}
            setNameTemplateParts={setProcedureRunNameTemplateParts}
            schemaPrefix={prefix}
          />
        </FieldWrapper>
      ) : (
        <>
          <SettingsLabel
            helpIcon={
              <NameTemplatesHelpIcon
                nameTemplateType={NameTemplateType.PROCEDURE_RUN}
                procedureType={procedureType}
              />
            }
            isRequired={false}
            label={`${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType)} run name template`}
          />
          <div className={css(styles.nameTemplateMessageText)}>
            {isClinicalOrPreclinical
              ? CUSTOMIZE_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE
              : SET_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE}
          </div>
        </>
      )}
    </Col>
  );
  const shouldRenderSchemaWarehouseField = !isBeingCreated && schema != null;

  const rows = [
    <Row>
      <>
        <Col>
          <FieldWrapper>
            <SchemaNameInput
              isWritable={true}
              schema={schema}
              onNameChange={setName}
              name={name}
              dataTestAttrs={{component: 'StudySchemaEditor', element: 'nameInput'}}
            />
          </FieldWrapper>
        </Col>
        {!isEqual(getEnabledStudyTypes(), [StudyType.CUSTOM]) && (
          <Col>
            <StudySchemaTypeDropdown
              value={studyType}
              onChange={setStudyType}
              isBeingCreated={isBeingCreated}
            />
          </Col>
        )}
        <Col>
          <FieldWrapper>
            <SchemaOwnerDisplay
              ownerId={ownerId}
              ownerChoices={ownerChoices}
              onOwnerChange={(owner: EntitySummary) => {
                setOwnerId(owner.id);
              }}
              schema={schema}
              dataTestAttrs={DATA_TEST_ATTRS.owner}
            />
          </FieldWrapper>
        </Col>
      </>
    </Row>,
    <Row>
      <>
        <StudySchemaIdGenerationSettings
          isBeingCreated={isBeingCreated}
          prefix={prefix}
          autoGenerateId={autoGenerateId}
          onIdGenerationChange={onIdGenerationChange}
          prefixDataTestAttrs={{component: 'StudySchemaEditor', element: 'prefixInput'}}
        />
        {/* The ID/Prefix group spans two grid columns, so this column carries its own left margin to
            land under the Owner column above (rather than the shared Col nth-child spacing). */}
        <div className={css(styles.nameTemplateColumn)}>
          {!isBeingCreated && schemaId != null ? (
            <FieldWrapper>
              <StudyNameTemplateField
                nameTemplateParts={studyNameTemplateParts}
                schemaFields={fields}
                schemaId={schemaId}
                isWritable={true}
                setNameTemplateParts={setStudyNameTemplateParts}
              />
            </FieldWrapper>
          ) : (
            <>
              <SettingsLabel
                label="Study name template"
                isRequired={false}
                helpIcon={
                  <NameTemplatesHelpIcon
                    nameTemplateType={NameTemplateType.STUDY}
                    procedureType={procedureType}
                  />
                }
              />
              <div className={css(styles.nameTemplateMessageText)}>
                {SET_NAMING_TEMPLATE_AFTER_SCHEMA_CREATION_MESSAGE}
              </div>
            </>
          )}
        </div>
      </>
    </Row>,
  ];

  // The run name template field is always rendered in its own row (rather than alongside the ID,
  // prefix, and study name template columns above) since that row is already full at 3 columns.
  if (maybeRunNameTemplateField || shouldRenderSchemaWarehouseField) {
    const cols = [];

    if (maybeRunNameTemplateField) {
      cols.push(maybeRunNameTemplateField);
    }
    if (shouldRenderSchemaWarehouseField) {
      // Schema warehouse field is only rendered when editing the study schema, which is when setSqlIdentifier
      // is passed into this component
      assert(setSqlIdentifier, 'Expected editable Sql identifier field.');
      cols.push(
        <Col key="SchemaWarehouseNameFieldColumn">
          <SchemaWarehouseNameField
            onChange={setSqlIdentifier}
            isWritable={true}
            sqlIdentifier={schema.sqlIdentifier}
          />
        </Col>
      );
    }
    rows.push(
      <Row>
        <>{cols}</>
      </Row>
    );
  }

  return (
    // TODO(BNCH-64599) extract styles into shared Schemas components
    <div
      className="u-flex-grow u-min-width-zero"
      {...getTestProps({component: 'StudySchemaMetadataSettings', element: 'root'})}
    >
      <div className="settingsPageContainer-section">
        {rows.map((row, i) => (
          <div key={i}>{row}</div>
        ))}
        <Row>
          <Col>
            <SchemaDescriptionInput
              value={schemaDescription}
              onChange={setSchemaDescription}
              isWritable={true}
              isBeingCreated={isBeingCreated}
            />
          </Col>
        </Row>
      </div>
      <div className={css(styles.settingsGroup)}>
        {/* TODO(BNCH-64042) - fill in isDisabled properly */}
        <FieldTable
          schemaId={schemaId}
          isBeingCreated={isBeingCreated}
          schemaHasData={false}
          isDisabled={false}
          itemClass={StudySchemaFieldTableRow}
          fields={fields}
          onFieldsChange={setFields}
          schemaType="study"
          batchSchemas={[]}
          storableSchemas={linkableSchemas.storableSchemas}
          assaySchemas={[]}
          ownerUrlId={getOwnerById(ownerId)?.url_identifier || ''}
          ownerId={ownerId}
          shouldShowIsRequired={true}
          shouldShowIsIntegration={false}
          shouldShowIsParentLink={false}
          shouldShowTooltipText={true}
          dataTestAttrs={{component: 'StudySchemaCreateFlow', element: 'fieldTable'}}
          savingState={SavingState.COMPLETED}
        />
      </div>
      {studyType === StudyType.CUSTOM && (
        <StudySchemaEntryTemplatesTable
          entryTemplates={entryTemplates}
          onAddClick={handleOnAddClick}
          writable={true}
        />
      )}
      {getTypedStudyAdapter(studyType).allowRequireDesignApproval && (
        <div className={css(styles.settingsGroup)}>
          <StudySchemaGeneralSettingsEditor
            isDesignApprovalRequired={isDesignApprovalRequired}
            setIsDesignApprovalRequired={setIsDesignApprovalRequired}
          />
        </div>
      )}
      {handleNext ? (
        <TooltipWrapper tooltipTitle={error}>
          <Button
            className="u-small-spacing-right"
            buttonStyle={ButtonStyle.SECONDARY}
            onClick={handleNext}
            size={ButtonSize.MEDIUM}
            text="Next"
            disabled={error != null}
            dataTestAttrs={{component: 'StudySchemaMetadataSettings', element: 'nextButton'}}
          />
        </TooltipWrapper>
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  settingsGroup: {
    marginTop: '32px',
    marginBottom: '32px',
  },
  schemaMetadataRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: '32px',
  },
  schemaMetadataColumn: {
    flex: `0 0 ${SETTINGS_GRID_COLUMN_WIDTH_PERCENT}%`,
    minWidth: 0,
    ':after': {
      flex: '1 1 auto',
    },
    ':nth-child(2)': {
      margin: `0 ${SETTINGS_GRID_COLUMN_GAP_PERCENT}% 0 ${SETTINGS_GRID_COLUMN_GAP_PERCENT}%`,
    },
  },
  schemaMetadataField: {
    width: 'auto',
    marginBottom: '0',
  },
  infoIcon: {
    color: SILVER,
    paddingLeft: 4,
  },
  nameTemplateMessageText: {
    color: GRAY_DARKER,
    paddingTop: 3,
  },
  // Sits after the ID/Prefix group (which spans the first two grid columns), aligned under the Owner column.
  nameTemplateColumn: {
    flex: `0 0 ${SETTINGS_GRID_COLUMN_WIDTH_PERCENT}%`,
    minWidth: 0,
    marginLeft: `${SETTINGS_GRID_COLUMN_GAP_PERCENT}%`,
  },
});
